<template>
  <div>
    <RightSideModal
      :show="openModel"
      :title="title"
      :subtitle="subtitle"
      @submit="validateAndSubmit"
      :submit-label="submitLabel"
      @dismiss="() => showDismissConfirmation = true"
      :submit-loading="submitting"
      :dismiss-label="$t('global.action.dismiss')"
    >
      <div slot="body">
        <IncrementalOnboardingAddBeneficiary
          :edit-mode="editMode"
          :i="i"
          :action-at-footer="true"
          :is-control-person="i === 0"
          :read-only="readOnly"
          :is-update="isUpdate"
        >
          <div></div>
        </IncrementalOnboardingAddBeneficiary>
      </div>
    </RightSideModal>
    <s-modal
      :control-variable="showDismissConfirmation"
      :primary-action="{
        label: $t('global.action.confirm'),
        action: () => dismiss()
      }"
      :secondary-action="{
        label: $t('global.action.dismiss'),
        action: () => {
          showDismissConfirmation = false;
        }
      }"
      :heading="$t('addBeneficiary.shouldDismiss')"
      size="small"
      :dismissable="false"
      class="s-text-left"
      action-variant="danger"
    >
      <template #body>
        <div class="s-text-left">
          {{ $t('addBeneficiary.shouldDismissDescription') }}
        </div>
      </template>
    </s-modal>
  </div>
</template>
<script>
import {mapActions, mapGetters} from 'vuex';
import validations from '@/app/validators/api/openapi';
import {
  additionalOwnerPercentageOwnership,
  controlPersonPercentageOwnership,
  StakeholderField
} from '@/store/modules/onboarding/stakeholderDetails';
import {EventBus} from '@/events/event-bus';
import {Country, FormEvents} from '@/app/utils/common/constants';
import BeneficialOwnerTypes from '@/data/beneficial-owner-types';
import {CountryCodeListSortedByName} from '@/data/country-code-list';
import IncrementalOnboardingAddBeneficiary from '@/components/onboarding/IncrementalOnboardingAddBeneficiary.vue';
import RightSideModal from '@/components/common/RightSideModal.vue';
import merchantOnboardingApi from '@/api/merchantOnboardingApi';
import {OnboardingRequest} from '@/api/paymentApi';

export default {
  name: 'AddBeneficiaryModal',
  components: {IncrementalOnboardingAddBeneficiary, RightSideModal},
  props: {
    openModel: {
      type: Boolean,
      required: false,
    },
    i: {
      type: Number,
      required: true,
    },
    onDismiss: {
      type: Function,
      required: true,
    },
    isControlPerson: Boolean,
    editMode: Boolean,
    readOnly: {
      type: Boolean,
      required: false,
    },
    isUpdate: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => {
    return {
      additionalOwnerPercentageOwnership,
      controlPersonPercentageOwnership,
      BeneficialOwnerTypes,
      submitting: false,
      showDismissConfirmation: false,
    };
  },
  computed: {
    title() {
      let section = `${
          this.isControlPerson ? 'controlPersonDetails' : 'beneficiaryDetails'
      }`;
      const mode = this.editMode ? 'edit' : 'add';
      if (!this.isControlPerson && this.getBeneficialOwnerType(this.i)) {
        return this.$t(`businessOwnership.${section}.${mode}.title`) 
            + " - " + this.$t(`businessOwnership.${section}.${this.getBeneficialOwnerType(this.i).value}`)
      }
      return this.$t(`businessOwnership.${section}.${mode}.title`);
    },
    subtitle() {
      let section = `${
          this.isControlPerson ? 'controlPersonDetails' : 'beneficiaryDetails'
      }`;
      const mode = this.editMode ? 'edit' : 'add';
      return this.$t(`businessOwnership.${section}.${mode}.subtitle`);
    },
    submitLabel() {
      if (this.readOnly) {
        return undefined;
      }
      return this.title;
    },
    personDetailsTitle() {
      return this.isControlPerson
          ? this.$t('addBeneficiary.personalDetails')
          : this.$t('addBeneficiary.beneficialOwnerDetails');
    },
    personDetailsInstructions() {
      return this.isControlPerson
          ? this.$t('addBeneficiary.personalDetailsInstructions')
          : this.$t('addBeneficiary.beneficialOwnerDetailsInstructions');
    },
    ownershipPercentage() {
      let section = `${
          this.isControlPerson ? 'controlPersonDetails' : 'beneficiaryDetails'
      }`;
      return this.$t(`businessOwnership.${section}.add.ownershipPercentage`);
    },
    maxDateOfBirth() {
      let date = new Date();
      date.setFullYear(date.getFullYear() - 18);
      return date;
    },
    ...mapGetters('app', ['getMerchantId']),
    ...mapGetters('stakeholdersDetails', [
      'getIncrementalOnboardignControlPerson',
      'getIncrementalOnboardingAdditionalBeneficiary',
      'getBeneficialOwnerType',
      'getBusinessName',
      'getAddressLine1',
      'getAddressLine2',
      'getAddressCity',
      'getAddressState',
      'getAddressZip',
      'getAddressCountry',
      'getFirstName',
      'getLastName',
      'getJobTitle',
      'getDateOfBirth',
      'getEmailAddress',
      'getPhoneNumber',
      'getOwnershipPercentage',
      'getTaxId',
      'getId',
      'getBeneficiaries',
      'shouldShowApiField',
      'getApi',
      'getErrors',
      'getErrorByField',
    ]),
    ...mapGetters('businessAddress', ['getCountry']),
  },
  mounted() {
    EventBus.$on(FormEvents.ENTER, this.validateAndSubmit);
  },
  methods: {
    ...mapActions('stakeholdersDetails', [
      'setOwnerType',
      'setBusinessName',
      'setFirstName',
      'setLastName',
      'setJobTitle',
      'setDateOfBirth',
      'setEmailAddress',
      'setPhoneNumber',
      'setAddressLine1',
      'setAddressLine2',
      'setAddressCity',
      'setAddressState',
      'setAddressZip',
      'setAddressCountry',
      'setOwnershipPercentage',
      'setTaxId',
      'setRegistrationNumber',
      'setVatNumber',
      'copyAdditionalBeneficiariesFromMerchantApiResponse',
      'validate',
      'setErrors',
      'clearErrors',
    ]),
    ...mapActions('merchantPayfacDetails', ['setValuesFromIncrementalOnboardingResponse']),
    getMax: function (key) {
      return validations[this.getApi][key].length?.maximum;
    },
    getMin: function (key) {
      return validations[this.getApi][key].length?.minimum;
    },
    getMaxValue: function (key) {
      return validations[this.getApi][key].range?.maximum;
    },
    getMinValue: function (key) {
      return validations[this.getApi][key].range?.minimum;
    },
    validateAndSubmit: function () {
      Promise.all([this.validate(this.i)]).then(async (values) => {
        if (values.some((x) => !x)) {
          this.$danger(this.$t('errors.validationErrorToastSave'));
        } else {
          await this.submitChanges();
        }
      });
    },
    async submitChanges() {
      this.submitting = true;

      let response;
      try {
        if (this.isControlPerson) {
          if (this.isUpdate) {
            response = await merchantOnboardingApi.updateControlPerson(
              this.getMerchantId, {
                ...this.getIncrementalOnboardignControlPerson,
                requestType: OnboardingRequest.RequestTypeEnum.ADDCONTROLPERSON,
              },
            );
          }
          else {
            response = await merchantOnboardingApi.addControlPerson(
              this.getMerchantId, {
                ...this.getIncrementalOnboardignControlPerson,
                requestType: OnboardingRequest.RequestTypeEnum.ADDCONTROLPERSON,
              },
            );
          }
        } else if (this.editMode) {
          const beneficiary = this.getBeneficiaries[this.i];
          const draftId = beneficiary[StakeholderField.DRAFT_ID].value;
          response = await merchantOnboardingApi.updateBeneficiary(
              this.getMerchantId,
              draftId,
              {
                ...this.getIncrementalOnboardingAdditionalBeneficiary(this.i),
                requestType: OnboardingRequest.RequestTypeEnum.UPDATEBENEFICIARY,
              },
          );
        } else {
          response = await merchantOnboardingApi.addBeneficiary(
              this.getMerchantId,
              {
                ...this.getIncrementalOnboardingAdditionalBeneficiary(this.i),
                requestType: OnboardingRequest.RequestTypeEnum.ADDBENEFICIARY,
              },
          );
          this.copyAdditionalBeneficiariesFromMerchantApiResponse(response.draft_merchant)
        }
        await this.setValuesFromIncrementalOnboardingResponse(response);
        this.$emit('close');
      } catch (error) {
        this.handleError(error);
      } finally {
        this.submitting = false;
      }
    },
    handleError(error) {
      if (error.source?.sub_errors?.length) {
        this.$danger('Validation failed!');
        error.source?.sub_errors?.forEach(subError => {
          this.setErrors({key: subError.field, errors: subError.message});
        });
      } else {
        this.$danger(error.source?.title);
      }
    },
    dismiss: function () {
      this.clearErrors();
      this.onDismiss();
      this.$emit('close');
      this.showDismissConfirmation = false;
    },
    taxId(i) {
      if (this.isControlPerson) {
        if (this.getCountry !== Country.USA) {
          return this.$t('addBeneficiary.taxId.socialSecurityNumber');
        }
        return this.$t('addBeneficiary.taxId.label');
      }
      const label =
          this.getBeneficialOwnerType(i).value === 'ENTITY'
              ? 'einAbbr'
              : 'ssnAbbr';
      if (this.getCountry !== Country.USA) {
        return this.$t('addBeneficiary.taxId.socialSecurityNumber');
      }
      return this.$t(`addBeneficiary.taxId.${label}`);
    },
    taxIdInstruction(i) {
      if (this.isControlPerson) {
        return this.$t('addBeneficiary.taxId.instruction');
      }
      const label =
          this.getBeneficialOwnerType(i).value === 'ENTITY' ? 'ein' : 'ssn';
      return this.$t(`addBeneficiary.taxId.${label}`);
    },
    getMaxLengthForTaxId() {
      return 20;
    },
    getFormattedDate: (date) => {
      if (date === null) return null;
      return date.replace(/(\d{4})-(\d\d)-(\d\d)/, '$2/$3/$1');
    },
    getPhoneCountryCode(i) {
      if (
          this.getPhoneNumber(i).value &&
          this.getPhoneNumber(i).value.includes(' ')
      ) {
        return this.getPhoneNumber(i).value?.split(' ')[0];
      }
      return CountryCodeListSortedByName.find(
          (x) => x.value === this.getCountry,
      )?.dialCode;
    },
  },
};
</script>

